import type {ReactElement} from 'react';
import {useAiChatLayout} from '@AiChat/hooks/useAiChatLayout';
import {AiChatChristmasAsset} from '@AiChat/lib/ai-chat-season-library.types';
import {AiChatChristmasAssetImage} from '@AiChat/components/ai-chat-christmas-asset-image/ai-chat-christmas-asset-image';
import {AiChatChristmasTree} from '@AiChat/components/ai-chat-christmas-tree/ai-chat-christmas-tree';
import {AiChatChristmasSnowman} from '@AiChat/components/ai-chat-christmas-snowman/ai-chat-christmas-snowman';
import {AiChatChristmasGifts} from '@AiChat/components/ai-chat-christmas-gifts/ai-chat-christmas-gifts';
import {assertNever} from '@Utils/assert-never.util';
import type {AiChatChristmasBackdropProps} from './ai-chat-christmas-backdrop.types';
import {AiChatChristmasBackdropPlacement} from './ai-chat-christmas-backdrop.types';
import styles from './ai-chat-christmas-backdrop.module.scss';

export function AiChatChristmasBackdrop({placement}: AiChatChristmasBackdropProps): ReactElement {
  const {showsSidebarRail} = useAiChatLayout();
  const isFloor = placement === AiChatChristmasBackdropPlacement.FLOOR;

  const getPlacementClassName = (): string => {
    switch (placement) {
      case AiChatChristmasBackdropPlacement.CARD:
        return styles.onCard;
      case AiChatChristmasBackdropPlacement.COMPACT_CARD:
        return styles.onCompactCard;
      case AiChatChristmasBackdropPlacement.PROMPT_BOX:
        return styles.onPromptBox;
      case AiChatChristmasBackdropPlacement.FLOOR:
        return getFloorClassName();
      default:
        return assertNever(placement);
    }
  };

  const getFloorClassName = (): string => {
    if (showsSidebarRail) {
      return `${styles.onFloor} ${styles.besideRail}`;
    }

    return styles.onFloor;
  };

  const getSnowPile = (pileClassName: string): ReactElement | null => {
    if (isFloor) {
      return null;
    }

    return getArt(AiChatChristmasAsset.SNOW_BANK, pileClassName);
  };

  const getFloorDrift = (driftClassName: string): ReactElement | null => {
    if (!isFloor) {
      return null;
    }

    return getArt(AiChatChristmasAsset.SNOW_FLOOR, driftClassName);
  };

  const getTree = (): ReactElement | null => {
    if (!hasRoomForTree()) {
      return null;
    }

    return <AiChatChristmasTree />;
  };

  const hasRoomForTree = (): boolean => {
    return placement !== AiChatChristmasBackdropPlacement.COMPACT_CARD && placement !== AiChatChristmasBackdropPlacement.PROMPT_BOX;
  };

  const getArt = (asset: AiChatChristmasAsset, artClassName: string): ReactElement => {
    return <AiChatChristmasAssetImage asset={asset} className={`${styles.art} ${artClassName}`} />;
  };

  return (
    <div className={`${styles.backdrop} ${getPlacementClassName()}`}>
      {getFloorDrift(styles.backDrift)}
      <div className={`${styles.perch} ${styles.leftPerch}`} aria-hidden="true">
        {getSnowPile(styles.snowPile)}
        <div className={styles.perchArt}>
          <AiChatChristmasSnowman />
        </div>
        {getSnowPile(styles.snowLip)}
      </div>
      <div className={`${styles.perch} ${styles.rightPerch}`}>
        {getSnowPile(styles.snowPile)}
        <div className={`${styles.perchArt} ${styles.treeAndGifts}`}>
          {getTree()}
          <AiChatChristmasGifts />
        </div>
        {getSnowPile(styles.snowLip)}
      </div>
      {getFloorDrift(styles.frontDrift)}
    </div>
  );
}
